import React, { useState, useCallback } from 'react';
import {
  Button,
  Datagrid,
  TextField,
  DateField,
  Pagination,
  TextInput,
  Filter,
  SelectInput,
  SelectField,
  useListContext,
  TopToolbar,
  List
} from 'react-admin';
import {
  Grid,
  Typography,
  makeStyles,
  CircularProgress
} from '@material-ui/core/';
import UserActivityDetailDialog from './UserActivityDetailDialog';
import WrapperField from '../../../core/components/WrapperField';
import moment from 'moment';
import DateInput from '../../../core/components/forked/input/DateInput';
import { activityTypes } from './ActivityLookups';

const useStyles = makeStyles({
  headerContainer: {
    paddingBottom: '8px'
  },
  root: {
    marginRight: 'auto'
  },
  dateFilterStyle: {
    display: 'flex',
    marginBottom: '4px'
  },
  dateInputStyle: { width: '145px', marginRight: '5px' },
  dateRangeSeparator: {
    marginTop: '25px',
    marginRight: '5px',
    color: '#757575'
  },
  progress: {
    display: 'flex',
    justifyContent: 'center',
    minHeight: '200px'
  },
  paddingCheckbox: {
    display: 'none'
  },
  headerCell: {
    '& tr': {
      '& th:nth-of-type(1)': {
        display: 'none'
      }
    }
  },
  tbody: {
    '& td:nth-of-type(1)': {
      display: 'none'
    }
  },
  listPage: {
    margin: '0'
  },
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    margin: '8px 0 0 10px'
  },
  button: {
    color: '#2962FF'
  }
});

const formatUploadDateFilter = (value, type) => {
  return value
    ? type === 'from'
      ? moment(value)
          .startOf('day')
          .toISOString()
      : moment(value)
          .endOf('day')
          .toISOString()
    : null;
};

const TabSectionLabel = ({ label }) => {
  const classes = useStyles();

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.headerContainer}>
          {label}
        </Typography>
      </Grid>
    </Grid>
  );
};

const SubjectColumn = props => {
  const { record } = props;
  return (
    <TextField
      source={
        record.activityType === 'FILE_ACCESSED'
          ? 'documentActivity.title'
          : 'subject'
      }
      {...props}
    />
  );
};

const DetailsColumn = props => {
  const { record, handleOpen } = props;
  const classes = useStyles();
  const hasDetails =
    record.activityType === 'EMAIL' || record.activityType === 'NOTE';

  if (record.activityType === 'FILE_ACCESSED') {
    return (
      <>
        {'Fund ID: ' +
          record.documentActivity.fundId +
          ', Investor ID: ' +
          record.documentActivity.investorId +
          ', Batch ID: ' +
          record.documentActivity.batchId +
          ', Document ID: ' +
          record.documentActivity.reportId}
      </>
    );
  }

  return hasDetails ? (
    <Button
      onClick={() => handleOpen(record)}
      className={classes.button}
      label="See Details..."
    ></Button>
  ) : (
    <TextField record={record} source="details" />
  );
};

const UserActivityDatagrid = props => {
  const { classes, permissions, handleOpen, ...dataGridProps } = props;
  const { loading } = useListContext(props);

  return (
    <>
      {!loading && (
        <Datagrid
          {...dataGridProps}
          style={{ margin: '0px', width: '100%' }}
          classes={{ thead: classes.headerCell, tbody: classes.tbody }}
        >
          <DateField
            label="Date & Time"
            source="dateTime"
            showTime
            sortable={false}
          />
          <SelectField
            label="Activity Type"
            source="activityType"
            choices={activityTypes}
          />
          <SubjectColumn label="Subject" />
          <DetailsColumn label="Activity Details" handleOpen={handleOpen} />
        </Datagrid>
      )}
      {loading && (
        <div className={classes.progress}>
          <CircularProgress />
        </div>
      )}
    </>
  );
};

const setActivityFilters = (value, setFilters, filterValues) => {
  if (
    (value === 'LOGIN' || value === 'FILE_ACCESSED') &&
    filterValues.generalSearchField
  ) {
    setFilters({
      ...filterValues,
      activityTypes: value,
      generalSearchField: undefined
    });
    //special case: clear filter if activityTypes filter was LOGIN or FILE_ACCESSED and type specific filter fields were populated
  } else if (
    (filterValues.activityTypes === 'LOGIN' &&
      value !== 'LOGIN' &&
      (filterValues.loginIpAddress ||
        filterValues.loginBrowser ||
        filterValues.loginSystem ||
        filterValues.loginSubject)) ||
    (filterValues.activityTypes === 'FILE_ACCESSED' &&
      value !== 'FILE_ACCESSED' &&
      (filterValues.documentReportId ||
        filterValues.documentBatchId ||
        filterValues.documentTitle ||
        filterValues.documentFundId ||
        filterValues.documentInvestorId ||
        filterValues.documentAccessType))
  ) {
    setFilters({
      ...filterValues,
      activityTypes: value,
      loginIpAddress: undefined,
      loginBrowser: undefined,
      loginSystem: undefined,
      loginSubject: undefined,
      documentReportId: undefined,
      documentBatchId: undefined,
      documentTitle: undefined,
      documentFundId: undefined,
      documentInvestorId: undefined,
      documentAccessType: undefined
    });
  }
};

const DateRangeFilter = ({ classes }) => (
  <div className={classes.dateFilterStyle}>
    <DateInput
      className={classes.dateInputStyle}
      label="Start Date"
      source="dateTimeFrom"
      placeholder="From:"
      customParseValue={value => formatUploadDateFilter(value, 'from')}
    />
    <Typography variant="body1" className={classes.dateRangeSeparator}>
      -
    </Typography>
    <DateInput
      className={classes.dateInputStyle}
      label="End Date"
      source="dateTimeTo"
      placeholder="To:"
      customParseValue={value => formatUploadDateFilter(value, 'to')}
    />
  </div>
);

const ActivityFilters = props => {
  const classes = useStyles();
  const { filterValues, setFilters } = useListContext(props);

  return (
    <Filter variant="standard" {...props}>
      <SelectInput
        label="Activity Type"
        source="activityTypes"
        alwaysOn
        allowEmpty={false}
        choices={[{ id: '', name: 'All' }, ...activityTypes]}
        SelectProps={{
          displayEmpty: true
        }}
        onChange={event =>
          setActivityFilters(event.target.value, setFilters, filterValues)
        }
      />
      <DateRangeFilter
        alwaysOn
        classes={classes}
        label="Activity Date Range"
        source="activityDateFilter"
      />
      {(filterValues.activityTypes === 'EMAIL' ||
        filterValues.activityTypes === 'CALL' ||
        filterValues.activityTypes === 'NOTE') && (
        <TextInput
          source="generalSearchField"
          alwaysOn
          label="Search"
          options={{ autoComplete: 'off' }}
        />
      )}
      {filterValues.activityTypes === 'LOGIN' && (
        <TextInput
          label="Login IP Address"
          source="loginIpAddress"
          alwaysOn
          options={{ autoComplete: 'off' }}
        />
      )}
      {filterValues.activityTypes === 'LOGIN' && (
        <TextInput
          label="Login Browser"
          source="loginBrowser"
          alwaysOn
          options={{ autoComplete: 'off' }}
        />
      )}
      {filterValues.activityTypes === 'LOGIN' && (
        <TextInput
          label="Login System"
          source="loginSystem"
          alwaysOn
          options={{ autoComplete: 'off' }}
        />
      )}
      {filterValues.activityTypes === 'LOGIN' && (
        <TextInput
          label="Login Subject"
          source="loginSubject"
          alwaysOn
          options={{ autoComplete: 'off' }}
        />
      )}
      {filterValues.activityTypes === 'FILE_ACCESSED' && (
        <TextInput
          label="Subject/Document Title"
          source="documentTitle"
          alwaysOn
          options={{ autoComplete: 'off' }}
        />
      )}
      {filterValues.activityTypes === 'FILE_ACCESSED' && (
        <TextInput
          label="Batch ID"
          source="documentBatchId"
          alwaysOn
          options={{ autoComplete: 'off' }}
        />
      )}
      {filterValues.activityTypes === 'FILE_ACCESSED' && (
        <TextInput
          label="Document ID"
          source="documentReportId"
          alwaysOn
          options={{ autoComplete: 'off' }}
        />
      )}
      {filterValues.activityTypes === 'FILE_ACCESSED' && (
        <TextInput
          label="Fund ID"
          source="documentFundId"
          alwaysOn
          options={{ autoComplete: 'off' }}
        />
      )}
      {filterValues.activityTypes === 'FILE_ACCESSED' && (
        <TextInput
          label="Investor ID"
          source="documentInvestorId"
          alwaysOn
          options={{ autoComplete: 'off' }}
        />
      )}
      {/* {filterValues.activityTypes === 'FILE_ACCESSED' && (
        <SelectInput
          label="Acess Type"
          source="documentAccessType"
          alwaysOn
          allowEmpty={false}
          choices={[{ id: '', name: 'All' }, ...documentAccessTypes]}
          SelectProps={{
            displayEmpty: true
          }}
        />
      )} */}
    </Filter>
  );
};

const UserActivityAction = props => {
  const classes = useStyles();
  const { handleActivityAddButtonClick } = props;
  return (
    <TopToolbar>
      <Button
        color="primary"
        onClick={() => handleActivityAddButtonClick()}
        label="Add Note"
        className={classes.addButton}
      />
    </TopToolbar>
  );
};

const UserActivityTab = props => {
  const { userId, permissions, ...rest } = props;
  const classes = useStyles();
  const hasAddActivityPermission = permissions.includes(
    'AUTH_CREATE_EDIT_NOTE_USER_ACTIVITY'
  );
  const [open, setIsOpen] = useState(false);
  const [activityDetails, setActivityDetails] = useState(null);

  const handleOpen = useCallback(
    record => {
      if (record) {
        setActivityDetails(record);
      } else {
        setActivityDetails(null);
      }

      setIsOpen(true);
    },
    [setIsOpen, setActivityDetails]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setActivityDetails(null);
  }, [setIsOpen, setActivityDetails]);
  return (
    <>
      <TabSectionLabel label="Activities" />
      <WrapperField>
        <List
          resource="activities"
          pagination={<Pagination rowsPerPageOptions={[10, 15, 20]} />}
          filter={{
            userId: userId
          }}
          filters={<ActivityFilters />}
          sort={{ field: 'dateTime', order: 'DESC' }}
          perPage={15}
          basePath="/activities"
          title=" "
          actions={
            hasAddActivityPermission && (
              <UserActivityAction
                userId={userId}
                handleActivityAddButtonClick={handleOpen}
                hasAddActivityPermission={hasAddActivityPermission}
              />
            )
          }
          empty={<></>}
          className={classes.listPage}
        >
          <UserActivityDatagrid
            permissions={permissions}
            classes={classes}
            handleOpen={handleOpen}
            {...rest}
          />
        </List>
        <UserActivityDetailDialog
          handleClose={handleClose}
          open={open}
          record={activityDetails}
          userId={userId}
          operation={activityDetails ? 'Edit' : 'Create'}
          resource="activities"
          basePath="/activities"
          title=" "
        />
      </WrapperField>
    </>
  );
};
export default UserActivityTab;
